import api from '../api/axiosConfig';

export type TickerSearchResult = { symbol: string; name?: string };

export type SubscriptionItem = {
    id: number;
    ticker: string;
    ticker_name: string;
    channel: 'telegram';
    endpointId?: string | null; // telegram chat id
    isActive: boolean;
    lastSentFeedId?: number | null;
    lastSentAt?: string | null;
};

export type AlertItem = {
    id: number;
    title?: string;
    name?: string;
    isActive?: boolean;
};

export type CreateFromEventPayload = {
    eventId: string;
    title?: string;
    isActive?: boolean;
    sector?: string | null;
    channels?: { telegram?: boolean } | null;
    filters?: any | null;
};

export type UpsertSubPayload = {
    id?: number;
    ticker: string;
    channel: 'telegram';
    endpointId: string; // telegram chat id required
    isActive?: boolean;
};

export type CreateSubPayload = {
    ticker: string;
    endpointId: string;
};

export type ExtraOpts = {
    headers?: Record<string, string>;
};

export async function searchTickers(q: string): Promise<TickerSearchResult[]> {
    const term = (q || '').trim();
    if (!term) return [];

    const candidates: Array<{ url: string; paramsKey?: string }> = [
        { url: '/alerts/tickers/search', paramsKey: 'q' },
        { url: '/alerts/tickers', paramsKey: 'q' },
        { url: '/alerts/stocks/search', paramsKey: 'q' },
        { url: '/alerts/symbols/search', paramsKey: 'q' },
    ];

    let lastErr: any = null;
    for (const c of candidates) {
        try {
            const res = await api.get(c.url, {
                params: c.paramsKey ? { [c.paramsKey]: term } : undefined,
            });
            const data: any = res.data;

            const list: any[] =
                Array.isArray(data)
                    ? data
                    : Array.isArray(data?.items)
                        ? data.items
                        : Array.isArray(data?.results)
                            ? data.results
                            : [];

            const norm = list
            .map((x) => {
                const symbol =
                    x?.symbol ??
                    x?.ticker ??
                    x?.code ??
                    x?.Symbol ??
                    x?.Ticker ??
                    x?.CODE ??
                    '';
                const name =
                    x?.name ??
                    x?.companyName ??
                    x?.Name ??
                    x?.ko_name ??
                    x?.en_name ??
                    undefined;
                return {
                    symbol: String(symbol).toUpperCase(),
                    name: name ? String(name) : undefined,
                };
            })
            .filter((x) => x.symbol);

            const seen = new Set<string>();
            const uniq: TickerSearchResult[] = [];
            for (const it of norm) {
                if (!seen.has(it.symbol)) {
                    seen.add(it.symbol);
                    uniq.push(it);
                }
            }
            return uniq.slice(0, 20);
        } catch (e: any) {
            lastErr = e;
            continue;
        }
    }
    throw lastErr || new Error('Ticker search endpoint not found');
}

export async function fetchEventAlerts(
    extra?: ExtraOpts & { sector?: string },
): Promise<AlertItem[]> {
    const res = await api.get('/alerts', {
        params: extra?.sector ? { sector: extra.sector } : undefined,
        headers: extra?.headers,
    });
    const data: any = res.data;
    const list: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
            ? data.items
            : [];

    return list
    .map((x) => ({
        id: Number(x?.id ?? x?.eventId ?? x?.alertId),
        title: x?.title ?? x?.name,
        name: x?.name,
        isActive: Boolean(x?.isActive ?? true),
    }))
    .filter((a) => !Number.isNaN(a.id));
}

export async function deleteEventAlert(id: number, extra?: ExtraOpts) {
    await api.delete(`/alerts/${id}`, { headers: extra?.headers });
    return true;
}

export async function fetchSubscriptions(extra?: ExtraOpts): Promise<SubscriptionItem[]> {
    const res = await api.get('/alerts/subscriptions', {
        headers: extra?.headers,
    });

    const data: any = res.data;
    const list: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
            ? data.items
            : [];

    return list
    .map((s) => {
        const ticker = String(s?.ticker ?? s?.symbol ?? '').trim();
        const tickerNameRaw =
            s?.ticker_name ??
            s?.symbol_name ??
            s?.companyName ??
            s?.company_name ??
            s?.name ??
            '';
        return {
            id: Number(s?.id),
            ticker,
            ticker_name: tickerNameRaw ? String(tickerNameRaw).trim() : ticker,
            channel: 'telegram' as const,
            endpointId: s?.endpointId ?? null,
            isActive: Boolean(s?.isActive ?? true),
            lastSentFeedId: (s?.lastSentFeedId ?? null) as number | null,
            lastSentAt: s?.lastSentAt ?? null,
        } as SubscriptionItem;
    })
    .filter((s) => s.id && s.ticker);
}

export async function deactivateSubscription(id: number, extra?: ExtraOpts) {
    await api.put(
        `/alerts/subscriptions/${id}`,
        { isActive: false },
        { headers: extra?.headers },
    );
    return true;
}

export async function reactivateSubscription(id: number, extra?: ExtraOpts) {
    await api.put(
        `/alerts/subscriptions/${id}`,
        { isActive: true },
        { headers: extra?.headers },
    );
    return true;
}

export async function deleteSubscription(id: number, extra?: ExtraOpts) {
    await api.delete(`/alerts/subscriptions/${id}`, {
        headers: extra?.headers,
    });
    return true;
}

async function upsertSubscriptionImpl(payload: UpsertSubPayload, extra?: ExtraOpts) {
    const endpointId = (payload.endpointId ?? '').trim();
    if (!endpointId) {
        throw new Error('텔레그램 Chat ID가 필요합니다.');
    }

    const body = {
        ticker: payload.ticker,
        channel: 'telegram' as const,
        endpointId,
        isActive: typeof payload.isActive === 'boolean' ? payload.isActive : true,
    };

    const res = await api.post('/alerts/subscriptions', body, {
        headers: extra?.headers,
    });
    return res.data ?? { ok: true };
}

export const upsertSubscription =
    upsertSubscriptionImpl as unknown as (...args: any[]) => any;

async function createFromEventImpl(payload: CreateFromEventPayload, extra?: ExtraOpts) {
    const body: any = {
        eventId: payload.eventId,
        title: payload.title,
        isActive: payload.isActive ?? true,
        channels: { telegram: true },
    };
    if (typeof payload.sector !== 'undefined') body.sector = payload.sector;
    if (typeof payload.filters !== 'undefined') body.filters = payload.filters;

    const res = await api.post('/alerts/from-event', body, {
        headers: extra?.headers,
    });
    return res.data ?? { ok: true };
}

export const createFromEvent =
    createFromEventImpl as unknown as (...args: any[]) => any;

export async function createTickerSubscription(
    payload: CreateSubPayload,
    extra?: ExtraOpts,
) {
    return upsertSubscription(
        {
            ticker: payload.ticker,
            channel: 'telegram',
            endpointId: payload.endpointId,
            isActive: true,
        },
        extra,
    );
}

export const listAlerts = (fetchEventAlerts as unknown) as (
    ...args: any[]
) => any;

export const deleteAlert = (deleteEventAlert as unknown) as (
    ...args: any[]
) => any;

export const listSubscriptions = (fetchSubscriptions as unknown) as (
    ...args: any[]
) => any;

export const reactivateSubscriptionAlias = (
    reactivateSubscription as unknown
) as (...args: any[]) => any;

export const deleteSubscriptionAlias = (
    deleteSubscription as unknown
) as (...args: any[]) => any;